import {
	LuIcon,
	Menu,
	type MenuClickTriggerProps,
	type MenuRootProps,
	type MenuVariantProps,
	toast,
} from "@overmap-ai/blocks"
import { type FormSubmission, OrganizationAccessLevel, type Stored, ERROR_MSG_UNEXPECTED } from "@overmap-ai/common"
import { APIError } from "@overmap-ai/core"
import { useOpenState } from "@overmap-ai/hooks"
import { memo, type ReactElement, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"
import type { FormSubmissionPdfExportOptions } from "../../typings/reports"
import { FormSubmissionPdfExportOptionsDialog } from "../Dialogs/FormSubmissionPdfExportOptionsDialog"
import { OrganizationAccessRequired } from "../OrganizationAccessRequired/AccessRequired"

interface FormSubmissionDropdownMenuProps
	extends MenuRootProps, Omit<MenuClickTriggerProps, "children">, MenuVariantProps {
	formSubmission: Stored<FormSubmission>
	onDelete: (formSubmission: Stored<FormSubmission>) => void
	onEdit: (formSubmission: Stored<FormSubmission>) => void
	children: ReactElement
}

export const FormSubmissionDropdownMenu = memo((props: FormSubmissionDropdownMenuProps) => {
	const { children, disabled, formSubmission, onDelete, onEdit, variant, size, radius, accentColor } = props

	const handleEdit = useCallback(() => {
		onEdit(formSubmission)
	}, [onEdit, formSubmission])

	const handleDelete = useCallback(() => {
		onDelete(formSubmission)
	}, [onDelete, formSubmission])

	const [showPdfExportOptionsDialog, openPdfExportOptionsDialog, closePdfExportOptionsDialog] = useOpenState(false)

	const handleExportFormToPdf = useCallback(
		(options: FormSubmissionPdfExportOptions) => {
			void sdk.formSubmissions.getFormSubmissionPdf(formSubmission.uuid, options).catch((error: unknown) => {
				toast.error({
					title: "Failed to download PDF",
					description: error instanceof APIError ? error.message : ERROR_MSG_UNEXPECTED,
				})
			})
		},
		[formSubmission.uuid],
	)

	return (
		<>
			<Menu.Root {...props}>
				<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
				<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
					<Menu.Group>
						<Menu.Item onSelect={handleEdit}>
							<LuIcon icon={modelIconMapping.edit} />
							Edit
						</Menu.Item>
						<Menu.Item onSelect={openPdfExportOptionsDialog}>
							<LuIcon icon={modelIconMapping.pdf} />
							Export to PDF
						</Menu.Item>
						<OrganizationAccessRequired accessLevel={OrganizationAccessLevel.ADMIN} type="hide">
							<>
								<Menu.Separator />
								<Menu.Item onSelect={handleDelete}>
									<LuIcon icon={modelIconMapping.delete} />
									Delete
								</Menu.Item>
							</>
						</OrganizationAccessRequired>
					</Menu.Group>
				</Menu.Content>
			</Menu.Root>
			<FormSubmissionPdfExportOptionsDialog
				key={formSubmission.form}
				formId={formSubmission.form}
				onSubmitOptions={handleExportFormToPdf}
				open={showPdfExportOptionsDialog}
				onOpenChange={closePdfExportOptionsDialog}
			/>
		</>
	)
})
FormSubmissionDropdownMenu.displayName = "FormSubmissionDropdownMenu"
